import type { Author } from '@/lib/content-types';

import { absoluteUrl, SITE_NAME, siteUrl } from '@/lib/site-url';
import { publisherLogoUrl } from '@/lib/seo';

type AuthorArticleRef = {
    title: string;
    slug: string;
    canonical?: string | null;
};

/**
 * `ProfilePage` + `Person` payload for the author profile view, plus a
 * compact `ItemList` of recent bylines so Google can attribute the
 * author's work directly to this canonical URL.
 */
export function buildAuthorProfileJsonLd(
    author: Author,
    recentArticles: AuthorArticleRef[] = [],
): Record<string, unknown> {
    const pageUrl = absoluteUrl(`/author/${encodeURIComponent(author.$id)}`);
    const origin = siteUrl();

    const sameAs = [
        author.facebook,
        author.twitter,
        author.instagram,
        author.pinterest,
    ]
        .map(ensureProtocol)
        .filter((u): u is string => !!u);

    const person: Record<string, unknown> = {
        '@type': 'Person',
        '@id': `${pageUrl}#person`,
        name: author.name || SITE_NAME,
        url: pageUrl,
    };
    if (author.firstName) person.givenName = author.firstName;
    if (author.lastName) person.familyName = author.lastName;
    if (author.title) person.jobTitle = author.title;
    if (author.biography) person.description = author.biography;
    if (author.profileUrl) person.image = author.profileUrl;
    if (author.email) person.email = author.email;
    if (sameAs.length > 0) person.sameAs = sameAs;
    person.worksFor = {
        '@type': 'NewsMediaOrganization',
        '@id': `${origin}/#organization`,
        name: SITE_NAME,
        url: origin,
    };

    const articleItems = recentArticles
        .slice(0, 10)
        .map((article, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: article.canonical?.trim()
                || absoluteUrl(`/article/${encodeURIComponent(article.slug)}`),
            name: article.title,
        }));

    const payload: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        '@id': `${pageUrl}#profile`,
        url: pageUrl,
        inLanguage: 'he-IL',
        dateCreated: author.$createdAt,
        dateModified: author.$updatedAt,
        mainEntity: person,
        about: person,
        isPartOf: { '@id': `${origin}/#website` },
        publisher: {
            '@type': 'NewsMediaOrganization',
            '@id': `${origin}/#organization`,
            name: SITE_NAME,
            url: origin,
            logo: {
                '@type': 'ImageObject',
                url: publisherLogoUrl(),
                width: 512,
                height: 512,
            },
        },
    };

    if (articleItems.length > 0) {
        payload.hasPart = {
            '@type': 'ItemList',
            name: `כתבות מאת ${author.name || SITE_NAME}`,
            numberOfItems: articleItems.length,
            itemListOrder: 'https://schema.org/ItemListOrderDescending',
            itemListElement: articleItems,
        };
    }

    return payload;
}

function ensureProtocol(url: string | undefined): string | undefined {
    if (!url) return undefined;
    const trimmed = url.trim();
    if (!trimmed) return undefined;
    if (/^[a-z]+:\/\//i.test(trimmed) || trimmed.startsWith('mailto:')) return trimmed;
    return `https://${trimmed.replace(/^\/+/, '')}`;
}
